// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📚 EXERCISE LIBRARY COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  getPersonalExercises,
  getClubExercises,
  getGlobalExercises,
  cloneExercise,
} from '../../services/exercises';
import type { Exercise } from '../../types';
import './ExerciseLibrary.css';

interface ExerciseLibraryProps {
  clubId?: number;
  onCreateExercise?: () => void;
  onEditExercise?: (exercise: Exercise) => void;
  onStartSession?: (exercise: Exercise) => void;
}

export const ExerciseLibrary: React.FC<ExerciseLibraryProps> = ({
  clubId,
  onCreateExercise,
  onEditExercise,
  onStartSession,
}) => {
  const { user, player, isGlobalAdmin } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'personal' | 'club' | 'global'>('personal');
  const [personalExercises, setPersonalExercises] = useState<Exercise[]>([]);
  const [clubExercises, setClubExercises] = useState<Exercise[]>([]);
  const [globalExercises, setGlobalExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExercises();
  }, [user, clubId]);

  const loadExercises = async () => {
    if (!player) return;

    setLoading(true);
    setError(null);

    try {
      // Load personal exercises
      const personal = await getPersonalExercises(player.id);
      setPersonalExercises(personal);

      // Load club exercises
      if (clubId) {
        // Specific club context
        const club = await getClubExercises(clubId);
        setClubExercises(club);
      } else {
        // Load exercises from ALL clubs the user is member of
        const { getUserClubMemberships } = await import('../../services/clubs');
        const userMemberships = await getUserClubMemberships();
        
        const allClubExercises: Exercise[] = [];
        for (const membership of userMemberships) {
          const exercises = await getClubExercises(membership.club_id);
          allClubExercises.push(...exercises);
        }
        setClubExercises(allClubExercises);
      }

      // Load global exercises
      const global = await getGlobalExercises();
      setGlobalExercises(global);
    } catch (err) {
      console.error('Error loading exercises:', err);
      setError('Failed to load exercises');
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (exerciseId: number) => {
    if (!player) return;

    try {
      await cloneExercise(exerciseId, player.id);
      loadExercises();
      alert(t('training.exercises.cloneSuccess'));
    } catch (err) {
      console.error('Error cloning exercise:', err);
      alert(t('training.exercises.cloneFailed'));
    }
  };

  const renderExerciseCard = (exercise: Exercise, canEdit: boolean, canClone: boolean) => (
    <div 
      key={exercise.id} 
      className="exercise-card"
      onClick={() => onStartSession?.(exercise)}
      style={{ cursor: onStartSession ? 'pointer' : 'default' }}
    >
      <div className="exercise-header">
        <h3>{exercise.title}</h3>
        {exercise.difficulty && (
          <span className={`difficulty-badge ${exercise.difficulty}`}>
            {t(`training.difficulty.${exercise.difficulty}`)}
          </span>
        )}
      </div>

      {exercise.description && (
        <p className="exercise-description">{exercise.description}</p>
      )}

      {exercise.category && (
        <span className="category-badge">
          {t(`training.category.${exercise.category}`)}
        </span>
      )}

      <div className="exercise-footer">
        <div className="exercise-meta">
          <span>⭐ {exercise.average_rating?.toFixed(1) || 'N/A'}</span>
          <span>📊 {exercise.usage_count} {t('training.exercises.uses')}</span>
        </div>

        <div className="exercise-actions" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <button 
              onClick={() => onEditExercise?.(exercise)}
              className="btn-icon"
              title={t('common.edit')}
            >
              ✏️
            </button>
          )}
          {canClone && (
            <button 
              onClick={() => handleClone(exercise.id)}
              className="btn-icon"
              title={t('training.exercises.clone')}
            >
              📋
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const currentExercises = activeTab === 'personal' 
    ? personalExercises 
    : activeTab === 'club' 
    ? clubExercises 
    : globalExercises;

  const canEditCurrent = activeTab === 'personal' || (activeTab === 'global' && isGlobalAdmin);
  const canCloneCurrent = activeTab !== 'personal';

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="exercise-library">
      <div className="library-header">
        <h2>{t('training.exercises.library')}</h2>
        {onCreateExercise && (
          <button onClick={onCreateExercise} className="btn-primary">
            ➕ {t('training.exercises.create')}
          </button>
        )}
      </div>

      <div className="library-tabs">
        <button
          className={activeTab === 'personal' ? 'active' : ''}
          onClick={() => setActiveTab('personal')}
        >
          👤 {t('training.exercises.personal')} ({personalExercises.length})
        </button>

        <button
          className={activeTab === 'club' ? 'active' : ''}
          onClick={() => setActiveTab('club')}
        >
          🏛️ {t('training.exercises.club')} ({clubExercises.length})
        </button>

        <button
          className={activeTab === 'global' ? 'active' : ''}
          onClick={() => setActiveTab('global')}
        >
          🌍 {t('training.exercises.global')} ({globalExercises.length})
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="exercises-grid">
        {currentExercises.length === 0 ? (
          <div className="empty-state">
            <p>{t('training.exercises.empty')}</p>
            {activeTab === 'personal' && onCreateExercise && (
              <button onClick={onCreateExercise} className="btn-primary">
                {t('training.exercises.createFirst')}
              </button>
            )}
          </div>
        ) : (
          currentExercises.map(exercise => 
            renderExerciseCard(exercise, canEditCurrent, canCloneCurrent)
          )
        )}
      </div>

      {activeTab === 'global' && isGlobalAdmin && (
        <div className="global-admin-actions">
          <button onClick={onCreateExercise} className="btn-secondary">
            🌍 {t('training.exercises.createGlobal')}
          </button>
        </div>
      )}
    </div>
  );
};

